import { Head } from '@components/common/head';
import { ComponentContainer, SectionSeparator } from '@styles/layout';
import React, { useEffect } from 'react';
import { PageProps } from '@model/pages';
import { CMSVideoGuides, Entry, TripTypes, VideoGuide } from '@model/contentful';
import { NavDestination } from '@model/content';
import { NavBarContainer } from '@components/navigation';
import { Place } from '@model/iceberg/geography';
import { useDispatch, useSelector } from 'react-redux';
import { Error404 } from '@components/common/hero/error/Error404';
import { Footer } from '@components/footer';
import { GuideBreadcrumb } from '@components/breadcrumbs';
import { Page } from '@model/common';
import ReactPlayer from 'react-player';
import styled from '@emotion/styled';
import { Theme, ThemeProps } from '@theme/base';
import { Grid } from '@mui/material';
import { GuideLink, GuidesLink } from '@components/common/next-link';
import { withTheme } from '@emotion/react';
import { aspectRatioPaddings, AspectRatios } from '@model/common/image';
import { CtaButton, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { useI18NextContext } from '@components/hooks';
import { withIsServer } from '@components/hoc';
import { setTripTypeFilter } from '@state/navigation';
import { getNavDestinationByPathAndTripType, getNavDestinationByPath } from '@state/cms';
import { SimpleTile } from '@components/common/simple-tile';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import SearchTabsBar from '@components/search/SearchTabsBar';
import { ERROR_CODES } from '@model/common/error-codes';

/* ***************** *
 *       Types       *
 * ***************** */
export interface VideoGuidePageLayoutProps extends PageProps {
  theme: Theme;
  cmsVideoGuide: VideoGuide;
  cmsVideoGuides: CMSVideoGuides;
  place: Place;
  path: string;
  tripType?: TripTypes;
  isServer: boolean;
}

/* ****************** *
 *       Styles       *
 * ****************** */

const VideoDescription: any = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  marginTop: theme.custom.spacing.xLarge,
  marginBottom: theme.custom.spacing.xLarge
}));

const MoreVideosGridContainer: any = styled(Grid)(({ theme }: ThemeProps) => ({
  ['&.MuiGrid-spacing-xs-4']: {
    marginTop: theme.custom.spacing.large
  }
}));

const VideoPlayerWrapper = styled.div({
  position: 'relative',
  paddingTop: aspectRatioPaddings[AspectRatios['16:9']]
});

/* *********************************** *
 *    VideoGuidePageLayoutComponent    *
 * *********************************** */

export const VideoGuidePageLayoutComponent = ({
  cmsVideoGuide,
  cmsVideoGuides,
  path,
  tripType,
  theme,
  isServer,
  promoBannerTripType
}: VideoGuidePageLayoutProps) => {
  const destination: NavDestination | undefined = useSelector(
    tripType ? getNavDestinationByPathAndTripType({ path, tripType }) : getNavDestinationByPath(path)
  );
  const destinationTitle: string = destination?.name || '';

  if (!cmsVideoGuide) return <Error404 code={ERROR_CODES.NO_VIDEO_GUIDE} />;

  const t: any = useI18NextContext();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setTripTypeFilter(tripType || TripTypes.ALL));
  }, []);

  const MAX_NUMBER_OF_TILES = 3;

  const { videoUrl, title, description } = cmsVideoGuide;

  const renderMoreVideoGuides = () =>
    cmsVideoGuides
      .filter((videoGuide: Entry<VideoGuide>) => videoGuide.fields.videoUrl !== videoUrl)
      .map((videoGuide: Entry<VideoGuide>, index: number) => {
        const {
          fields: { title, shortDescription, path: videoGuidePath, thumbnailImage }
        } = videoGuide;
        return (
          index < MAX_NUMBER_OF_TILES && (
            <Grid item xs={12} sm={6} lg={4} key={videoGuidePath}>
              <GuideLink path={path} guidePath={videoGuidePath} tripType={tripType} guidePage={Page.VIDEO_GUIDES}>
                <SimpleTile
                  title={title}
                  shortDescription={shortDescription}
                  imageUrl={thumbnailImage.fields.file.url}
                />
              </GuideLink>
            </Grid>
          )
        );
      });

  const moreVideos = renderMoreVideoGuides();

  return (
    <>
      <Head metaTitle={cmsVideoGuide.title} metaDescription={cmsVideoGuide.shortDescription} />
      <NavBarContainer promoBannerTripType={promoBannerTripType} renderSearch={<SearchTabsBar condensed />} />
      <GuideBreadcrumb
        destination={{ path, display: destinationTitle }}
        guide={cmsVideoGuide.path}
        guideDisplay={cmsVideoGuide.title}
        tripType={tripType}
        guidePage={Page.VIDEO_GUIDES}
      />
      <SectionSeparator />
      <ComponentContainer>
        <SectionTitle size={SectionTitleSizes.LARGE} heading={title} />
        <VideoDescription>{description}</VideoDescription>
        {!isServer && (
          <VideoPlayerWrapper suppressHydrationWarning>
            <ReactPlayer
              url={videoUrl}
              controls={true}
              width={'100%'}
              height={'100%'}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: theme.custom.colors.black
              }}
            />
          </VideoPlayerWrapper>
        )}
        <SectionSeparator />
        {!!moreVideos.length && (
          <>
            <SectionTitle heading={t('video-guides__label--move-videos-title', { destination: destinationTitle })} />
            <MoreVideosGridContainer container spacing={theme.custom.gridSpacing.default}>
              {moreVideos}
            </MoreVideosGridContainer>
            {moreVideos.length > MAX_NUMBER_OF_TILES && (
              <GuidesLink path={path} tripType={tripType} guidePage={Page.VIDEO_GUIDES}>
                <CtaButton
                  label={t('video-guides__label--view-more-videos')}
                  color={ButtonColor.SECONDARY}
                  variant={ButtonVariant.OUTLINED}
                  size={ButtonSize.REGULAR}
                />
              </GuidesLink>
            )}
          </>
        )}
      </ComponentContainer>
      <SectionSeparator />
      <Footer />
    </>
  );
};

export const VideoGuidePageLayout = withTheme(withIsServer(VideoGuidePageLayoutComponent));
